import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { EyewitnessPost, PostPetition } from "../../interface/api";
import { report_validate } from "../../validate/post";
import { useNotificationService } from "../../hooks/notification";
import { apiPostEyeReport } from "../../api/post";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HandsFistIcon, LoadingSpinner } from "../ui-elements/svg-icons";
import ImageGrid from "../image-grid";
import TypographyContent from "../ui-elements/typography-content";
import { PostReportModal, SendReportModal } from "./modal-post";
import { useDispatch } from "react-redux";

const ReportPreview: FC<{
  report_data: EyewitnessPost;
  close_modal: () => void;
  set_page_default: () => void;
  setReport_data: React.Dispatch<React.SetStateAction<EyewitnessPost>>;
}> = ({ report_data, setReport_data, close_modal, set_page_default }) => {
  const { showNotification } = useNotificationService();
  const dispatch = useDispatch();
  const [pre_submit, setPre_submit] = useState(false);
  const [submit_modal, setSubmit_modal] = useState(false);
  const queryClient = useQueryClient();
  const handle_pre_modal = () => {
    setPre_submit(!pre_submit);
  };
  const handle_submit_modal = () => {
    setSubmit_modal(!submit_modal);
  };

  const mutation = useMutation({
    mutationFn: (fields: EyewitnessPost) => apiPostEyeReport(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("details" in data) {
          showNotification(data?.details ?? "An error occurred", "error");
          return;
        }
        return;
      }
      if (data.status === 201) {
        showNotification(
          "Your report has been submitted successfully!",
          "success"
        );

        // **Clear and refetch dashboard data**
        queryClient.removeQueries({ queryKey: ["dashboard"] });
        queryClient.refetchQueries({ queryKey: ["dashboard"] });
        setReport_data({
          title: "",
          media: [],
          context: "",
          category: "",
          post_type: "eyewitness",
        });
        setSubmit_modal(true);
      }
    },
    onError: (error) => {
      console.error(error);
      showNotification(
        "An error occurred while submitting your report",
        "error"
      );
    },
  });

  const submit_report = () => {
    const { bool, text } = report_validate(report_data);
    if (!bool) {
      showNotification(text, "warning");
    } else {
      handle_pre_modal();
    }
  };

  const handle_submit = () => {
    const data = {
      ...report_data,
      category: report_data?.category?.toLowerCase(),
      media: report_data?.media?.map(
        (image: { data_url: string; file: File }) => image?.file
      ),
    };
    mutation.mutate(data);
    handle_pre_modal();
  };

  return (
    <Box>
      <PostReportModal
        open={pre_submit}
        modal_handler={handle_pre_modal}
        onSubmit={handle_submit}
      />
      <SendReportModal
        open={submit_modal}
        modal_handler={handle_submit_modal}
        close_modal={close_modal}
        set_page_default={set_page_default}
      />
      <Box
        sx={{
          width: "100%",
          height: "4px",
          background: "url(/assets/icons/posts/line-05.png)",
        }}
      />
      <Box sx={{ m: 3 }}>
        <Stack sx={{ my: 2 }}>
          <Typography
            color="black"
            variant="body2"
            sx={{
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {report_data.title}
          </Typography>
        </Stack>
        <Stack sx={{ m: 2 }}>
          <ImageGrid
            images={report_data.media?.map(
              (img_link: { data_url: string; file: File }) => img_link?.data_url
            )}
          />
        </Stack>
        <Box>
          <TypographyContent content={report_data.context} />
        </Box>
      </Box>
      <Stack sx={{ my: 3 }} direction={"row"} justifyContent={"center"}>
        {!mutation.isPending ? (
          <Button
            variant="contained"
            color="primary"
            onClick={submit_report}
            sx={{
              mt: 2,
              borderRadius: "40px",
              width: "80%",
              mx: "auto",
            }}
          >
            Submit Report
          </Button>
        ) : (
          <LoadingSpinner />
        )}
      </Stack>
    </Box>
  );
};

export default ReportPreview;
